/**
 * LaTeX AST parser — wraps latex-utensils to provide:
 *  • Parsed AST for any LaTeX source
 *  • Environment extraction (name + line range) for matching & folding
 *  • Error detection (missing \end, syntax errors)
 *  • Section-level structure for folding
 *
 * All public helpers work with 1-based line numbers (matching Monaco).
 */

import { latexParser } from "latex-utensils"
import type { Location, SyntaxError as PEGSyntaxError } from "latex-utensils/out/types/src/pegjs/pegjs_types"

/* ────────────────────────── Public types ────────────────────────── */

export interface EnvironmentSpan {
  name: string
  /** 1-based line of \begin{…} */
  startLine: number
  startCol: number
  /** 1-based line of \end{…} */
  endLine: number
  endCol: number
  /** Offset (0-based) into the source string for \begin */
  startOffset: number
  /** Offset (0-based) into the source string for \end */
  endOffset: number
  /** Nested child environments */
  children: EnvironmentSpan[]
}

export interface SectionSpan {
  level: number // 0 = \part … 6 = \subparagraph
  name: string
  startLine: number
  endLine: number // line before the next section at the same or higher level, or EOF
}

export interface LatexDiagnostic {
  startLine: number
  startCol: number
  endLine: number
  endCol: number
  message: string
  severity: "error" | "warning"
}

export interface ParseResult {
  /** True if the parser succeeded (possibly with recoverable issues) */
  ok: boolean
  environments: EnvironmentSpan[]
  sections: SectionSpan[]
  diagnostics: LatexDiagnostic[]
  /** Comments found in the source */
  comments: Array<{ line: number; text: string }>
}

/* ────────────────────────── Helpers ────────────────────────── */

const SECTION_COMMANDS: Record<string, number> = {
  part: 0,
  chapter: 1,
  section: 2,
  subsection: 3,
  subsubsection: 4,
  paragraph: 5,
  subparagraph: 6,
}

/* ── Regex-based fallback for robustness ── */

interface RawEnvMatch {
  kind: "begin" | "end"
  name: string
  line: number
  col: number
  offset: number
}

/**
 * Scan source with regex to find all \begin{…} and \end{…} occurrences.
 * This is used both as a fallback when the AST parser fails and as a
 * secondary pass to catch things the AST might miss.
 */
function regexScanEnvironments(source: string): RawEnvMatch[] {
  const results: RawEnvMatch[] = []
  const re = /\\(begin|end)\{([a-zA-Z*]+)\}/g
  let match: RegExpExecArray | null

  // Build a line→offset index for fast offset→line conversion
  const lineOffsets = buildLineOffsets(source)

  while ((match = re.exec(source)) !== null) {
    const offset = match.index
    const { line, col } = offsetToLineCol(lineOffsets, offset)
    results.push({
      kind: match[1] as "begin" | "end",
      name: match[2],
      line,
      col,
      offset,
    })
  }
  return results
}

function buildLineOffsets(source: string): number[] {
  const offsets = [0]
  for (let i = 0; i < source.length; i++) {
    if (source[i] === "\n") offsets.push(i + 1)
  }
  return offsets
}

function offsetToLineCol(lineOffsets: number[], offset: number): { line: number; col: number } {
  let lo = 0
  let hi = lineOffsets.length - 1
  while (lo < hi) {
    const mid = (lo + hi + 1) >> 1
    if (lineOffsets[mid] <= offset) lo = mid
    else hi = mid - 1
  }
  return { line: lo + 1, col: offset - lineOffsets[lo] + 1 }
}

/**
 * Build a nested environment tree from a flat list of begin/end matches.
 * Generates diagnostics for unmatched pairs.
 */
function buildEnvTree(
  matches: RawEnvMatch[],
  diagnostics: LatexDiagnostic[],
): EnvironmentSpan[] {
  const stack: { match: RawEnvMatch; children: EnvironmentSpan[] }[] = []
  const roots: EnvironmentSpan[] = []

  for (const m of matches) {
    if (m.kind === "begin") {
      stack.push({ match: m, children: [] })
    } else {
      // Find matching \begin on the stack
      let found = false
      for (let i = stack.length - 1; i >= 0; i--) {
        if (stack[i].match.name === m.name) {
          const opened = stack.splice(i, 1)[0]
          const span: EnvironmentSpan = {
            name: m.name,
            startLine: opened.match.line,
            startCol: opened.match.col,
            endLine: m.line,
            endCol: m.col,
            startOffset: opened.match.offset,
            endOffset: m.offset,
            children: opened.children,
          }
          // Attach to parent or root
          if (stack.length > 0) {
            stack[stack.length - 1].children.push(span)
          } else {
            roots.push(span)
          }
          found = true
          break
        }
      }
      if (!found) {
        diagnostics.push({
          startLine: m.line,
          startCol: m.col,
          endLine: m.line,
          endCol: m.col + `\\end{${m.name}}`.length,
          message: `Unmatched \\end{${m.name}} — no corresponding \\begin{${m.name}}`,
          severity: "error",
        })
      }
    }
  }

  // Anything left on the stack is an unmatched \begin
  for (const remaining of stack) {
    diagnostics.push({
      startLine: remaining.match.line,
      startCol: remaining.match.col,
      endLine: remaining.match.line,
      endCol: remaining.match.col + `\\begin{${remaining.match.name}}`.length,
      message: `Missing \\end{${remaining.match.name}}`,
      severity: "error",
    })
  }

  return roots
}

/**
 * Scan for section commands with regex for folding.
 */
function scanSections(source: string): SectionSpan[] {
  const lines = source.split("\n")
  const raw: { level: number; name: string; startLine: number }[] = []

  const sectionRe = /\\(part|chapter|section|subsection|subsubsection|paragraph|subparagraph)\*?\{([^}]*)\}/

  for (let i = 0; i < lines.length; i++) {
    const m = sectionRe.exec(lines[i])
    if (m) {
      const cmd = m[1]
      const level = SECTION_COMMANDS[cmd] ?? 2
      raw.push({ level, name: m[2], startLine: i + 1 })
    }
  }

  // Compute endLine for each section
  const sections: SectionSpan[] = []
  for (let i = 0; i < raw.length; i++) {
    let endLine = lines.length
    for (let j = i + 1; j < raw.length; j++) {
      if (raw[j].level <= raw[i].level) {
        endLine = raw[j].startLine - 1
        break
      }
    }
    sections.push({ ...raw[i], endLine })
  }

  return sections
}

/**
 * Scan for comments.
 */
function scanComments(source: string): Array<{ line: number; text: string }> {
  const lines = source.split("\n")
  const comments: Array<{ line: number; text: string }> = []
  const re = /(?:^|[^\\])(%.*$)/
  for (let i = 0; i < lines.length; i++) {
    const m = re.exec(lines[i])
    if (m) {
      comments.push({ line: i + 1, text: m[1] })
    }
  }
  return comments
}

/* ── Brace-matching diagnostics ── */

function checkBraces(source: string, diagnostics: LatexDiagnostic[]): void {
  const lineOffsets = buildLineOffsets(source)
  const stack: { char: string; offset: number }[] = []

  let inComment = false
  for (let i = 0; i < source.length; i++) {
    const ch = source[i]
    if (ch === "\n") {
      inComment = false
      continue
    }
    if (ch === "%" && (i === 0 || source[i - 1] !== "\\")) {
      inComment = true
      continue
    }
    if (inComment) continue

    // Skip escaped braces
    if (ch === "\\" && i + 1 < source.length && (source[i + 1] === "{" || source[i + 1] === "}")) {
      i++ // skip next
      continue
    }

    if (ch === "{") {
      stack.push({ char: ch, offset: i })
    } else if (ch === "}") {
      if (stack.length === 0 || stack[stack.length - 1].char !== "{") {
        const pos = offsetToLineCol(lineOffsets, i)
        diagnostics.push({
          startLine: pos.line,
          startCol: pos.col,
          endLine: pos.line,
          endCol: pos.col + 1,
          message: "Unmatched closing brace '}'",
          severity: "warning",
        })
      } else {
        stack.pop()
      }
    }
  }

  for (const remaining of stack) {
    const pos = offsetToLineCol(lineOffsets, remaining.offset)
    diagnostics.push({
      startLine: pos.line,
      startCol: pos.col,
      endLine: pos.line,
      endCol: pos.col + 1,
      message: "Unmatched opening brace '{'",
      severity: "warning",
    })
  }
}

/* ────────────────────────── Main parse function ────────────────────────── */

/**
 * Parse a LaTeX document and return structured information.
 *
 * Uses latex-utensils for AST when possible, with regex fallback for
 * robustness (so the editor never breaks on malformed input).
 */
export function parseLatex(source: string): ParseResult {
  const diagnostics: LatexDiagnostic[] = []

  // ── 1. Try AST-based parse ──
  let astOk = false
  try {
    const ast = latexParser.parse(source, {
      enableComment: true,
    })
    astOk = true

    // We still use regex for environment matching because the AST
    // parse can throw on partial/invalid documents, and the regex
    // approach gives us line-level info easily.
    void ast
  } catch (err: unknown) {
    // PEG.js SyntaxError — report it
    if (err && typeof err === "object" && "location" in err) {
      const syntaxErr = err as PEGSyntaxError
      diagnostics.push({
        startLine: syntaxErr.location.start.line,
        startCol: syntaxErr.location.start.column,
        endLine: syntaxErr.location.end.line,
        endCol: syntaxErr.location.end.column,
        message: syntaxErr.message || "LaTeX syntax error",
        severity: "error",
      })
    }
  }

  // ── 2. Regex-based environment scan (always runs — robust) ──
  const envMatches = regexScanEnvironments(source)
  const environments = buildEnvTree(envMatches, diagnostics)

  // ── 3. Section scan ──
  const sections = scanSections(source)

  // ── 4. Brace matching ──
  checkBraces(source, diagnostics)

  // ── 5. Comments ──
  const comments = scanComments(source)

  return {
    ok: astOk && diagnostics.filter((d) => d.severity === "error").length === 0,
    environments,
    sections,
    diagnostics,
    comments,
  }
}

/**
 * Flatten nested environments into a flat array for easy lookup.
 */
export function flattenEnvironments(envs: EnvironmentSpan[]): EnvironmentSpan[] {
  const result: EnvironmentSpan[] = []
  function walk(list: EnvironmentSpan[]) {
    for (const e of list) {
      result.push(e)
      walk(e.children)
    }
  }
  walk(envs)
  return result
}
